import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import api from './api';

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrateSession = useCallback(async () => {
    const response = await api.get('/college/overview');
    const nextOverview = response.data || null;
    setOverview(nextOverview);
    setUser(nextOverview ? {
      ...nextOverview.admin,
      college: nextOverview.college
    } : null);
    return nextOverview;
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        await hydrateSession();
      } catch (error) {
        setUser(null);
        setOverview(null);
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, []);

  const value = useMemo(
    () => ({
      user,
      overview,
      setOverview,
      setUser,
      refreshSession: hydrateSession,
      loading,
    }),
    [user, overview, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <CenteredPanel text="Checking access..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/access-request" element={<AccessRequest />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/approval-required" element={<ApprovalRequired />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Overview />} />
            <Route path="global-leaderboard" element={<GlobalLeaderboard />} />
            <Route path="college-leaderboard" element={<CollegeLeaderboard />} />
            <Route path="students" element={<Students />} />
            <Route path="assessments" element={<Assessments />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

function Landing() {
  useEffect(() => {
    document.title = 'RankMySkills - College Dashboard & Student Analytics';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute(
      'content',
      'Build placement confidence and lead student success. The definitive platform for tracking, benchmarking, and advancing student readiness.'
    );
  }, []);

  const advantages = [
    {
      icon: TargetIcon,
      title: 'Centralized student tracking',
      description: 'View skill growth, coding progress, and readiness status from one clean dashboard.'
    },
    {
      icon: RocketIcon,
      title: 'Placement focused insights',
      description: 'Identify high-potential students early and build targeted placement strategies.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Verified performance signals',
      description: 'Rely on trusted, scored data instead of manual sheets and fragmented reports.'
    },
    {
      icon: TrendingUpIcon,
      title: 'Real-time leaderboard view',
      description: 'Spot top performers by college and across the platform with live rankings.'
    },
    {
      icon: UsersIcon,
      title: 'Secure access workflow',
      description: 'Access is approval-based, ensuring college data remains controlled and protected.'
    },
    {
      icon: SchoolIcon,
      title: 'Faster academic decisions',
      description: 'Turn performance patterns into concrete actions for training and mentoring teams.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white selection:bg-red-800 selection:text-white overflow-hidden">
      <nav className="fixed w-full z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-red-800 to-red-800 rounded-lg flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-xl">R</span>
              </div>
              <span className="text-lg md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-700 to-red-700">
                RankMySkills
              </span>
            </div>
            
            <div className="flex items-center space-x-2 md:space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors px-2 py-1 md:px-4 md:py-2 text-sm md:text-base">
                Login
              </Link>
              <Link 
                to="/register" 
                className="bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 md:px-6 md:py-2 rounded-full font-medium transition-all hover:scale-105 transform flex items-center group text-sm md:text-base whitespace-nowrap"
              >
                Get Started
                <ArrowRightIcon className="ml-1 md:ml-2 w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative pt-32 pb-20 sm:pt-40 sm:pb-24 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-800/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-700/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div>
            <h1 className="text-5xl sm:text-7xl font-extrabold tracking-tight mb-8">
              Build Placement Confidence.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-700 to-red-700">
                Lead Student Success.
              </span>
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-400 mb-10">
              Track student progress, benchmark performance, and drive placement readiness from one trusted platform.
              <br />
              Turn verified data into faster interventions, stronger placements, and measurable college outcomes.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/access-request"
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-red-700 to-red-700 rounded-full text-lg font-bold hover:shadow-lg hover:shadow-red-800/30 transition-all transform hover:-translate-y-1"
              >
                Get Access
              </Link>
              <Link 
                to="/register"
                className="w-full sm:w-auto px-8 py-4 border border-white/20 rounded-full text-lg font-medium hover:bg-white/5 transition-all"
              >
                Register
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="py-24 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Why Colleges Choose RankMySkills</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Built for placement teams that want measurable outcomes, not guesswork.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {advantages.map((feature, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:border-red-800/50 transition-colors group hover:bg-white/10"
              >
                <div className="mb-4 p-3 bg-red-800/10 rounded-lg w-fit group-hover:bg-red-800/20 transition-colors">
                  <feature.icon className="w-8 h-8 text-red-800" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-gradient-to-r from-red-800 to-red-800 rounded-3xl p-12 text-center shadow-2xl shadow-red-800/50">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Start with verified access and unlock full student readiness.
            </h2>
            <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
              Request access today and activate your college dashboard in minutes.
            </p>
            <Link 
              to="/access-request"
              className="inline-flex items-center px-8 py-4 bg-white text-red-800 rounded-full font-bold text-lg hover:bg-gray-100 transition-all"
            >
              Get Access
              <ChevronRightIcon className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>

      <footer className="border-t border-white/10 bg-black py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
            <div className="w-6 h-6 bg-gradient-to-br from-red-800 to-red-800 rounded flex items-center justify-center">
              <span className="text-white font-bold text-xs">R</span>
            </div>
            <span className="text-xl font-bold text-white">RankMySkills</span>
          </div>
          <div className="text-gray-500 text-sm">
            © {new Date().getFullYear()} RankMySkills. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function IconBase({ className, children }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      {children}
    </svg>
  );
}

function ArrowRightIcon({ className }) {
  return (
    <IconBase className={className}>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </IconBase>
  );
}

function ChevronRightIcon({ className }) {
  return (
    <IconBase className={className}>
      <path d="m9 18 6-6-6-6" />
    </IconBase>
  );
}

function TargetIcon({ className }) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </IconBase>
  );
}

function RocketIcon({ className }) {
  return (
    <IconBase className={className}>
      <path d="M5 19c2-1 4-1 6 1" />
      <path d="M13 11 20 4c-1 6-4 10-9 13l-3-3c3-5 7-8 13-9" />
      <circle cx="15" cy="9" r="1.5" />
    </IconBase>
  );
}

function ShieldCheckIcon({ className }) {
  return (
    <IconBase className={className}>
      <path d="M12 3 5 6v6c0 5 3 8 7 9 4-1 7-4 7-9V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </IconBase>
  );
}

function TrendingUpIcon({ className }) {
  return (
    <IconBase className={className}>
      <path d="M3 17h18" />
      <path d="m6 14 4-4 3 3 5-6" />
      <path d="M18 7h-3" />
      <path d="M18 7v3" />
    </IconBase>
  );
}

function UsersIcon({ className }) {
  return (
    <IconBase className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="10" cy="7" r="3" />
      <path d="M20 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 4.13a4 4 0 0 1 0 7.75" />
    </IconBase>
  );
}

function SchoolIcon({ className }) {
  return (
    <IconBase className={className}>
      <path d="m3 10 9-5 9 5-9 5-9-5Z" />
      <path d="M7 12v5c0 1 2 2 5 2s5-1 5-2v-5" />
    </IconBase>
  );
}

function AccessRequest() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    college_id: '',
    designation: '',
    phone: '',
    message: '',
  });
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/auth/colleges');
        setColleges(response.data.colleges || []);
      } catch (err) {
        setError('Failed to load colleges.');
      }
    };
    load();
  }, []);

  const selectedCollege = colleges.find((college) => college.college_id === form.college_id);
  const alreadyAssigned = Boolean(selectedCollege?.has_approved_admin);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setStatus('');

    try {
      await api.post('/auth/request-college-admin', form);
      setStatus('Request submitted. You will receive an invite code after approval.');
      setForm({ name: '', email: '', college_id: '', designation: '', phone: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page auth">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Request college admin access</h2>
          <p className="muted">Submit your details to receive an invite code from RankMySkills.</p>
        </div>
        <form className="form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <Input label="Full name" name="name" value={form.name} onChange={handleChange} required />
            <Input label="Work email" name="email" type="email" value={form.email} onChange={handleChange} required />
            <Select label="College" name="college_id" value={form.college_id} onChange={handleChange} required>
              <option value="">Select college</option>
              {colleges.map((college) => (
                <option key={college.college_id} value={college.college_id}>
                  {college.name_display}
                </option>
              ))}
            </Select>
            <Input label="Designation" name="designation" value={form.designation} onChange={handleChange} required />
            <Input label="Phone" name="phone" value={form.phone} onChange={handleChange} required />
          </div>
          <TextArea label="Message (optional)" name="message" value={form.message} onChange={handleChange} />

          {alreadyAssigned ? (
            <div className="alert warning">
              This college already has an approved admin. Please contact the super admin to request changes.
            </div>
          ) : null}
          {status ? <div className="alert success">{status}</div> : null}
          {error ? <div className="alert error">{error}</div> : null}

          <button className="btn btn-primary" disabled={loading || alreadyAssigned} type="submit">
            {loading ? 'Submitting...' : 'Send Request'}
          </button>
        </form>
        <p className="muted small">
          Already have an invite code? <Link to="/register">Register here</Link>.
        </p>
      </div>
    </div>
  );
}

function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    college_id: '',
    city: '',
    state: '',
    invite_code: '',
  });
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/auth/colleges');
        setColleges(response.data.colleges || []);
      } catch (err) {
        setError('Failed to load colleges.');
      }
    };
    load();
  }, []);

  const selectedCollege = colleges.find((college) => college.college_id === form.college_id);
  const alreadyAssigned = Boolean(selectedCollege?.has_approved_admin);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setStatus('');

    try {
      await api.post('/auth/register/college', form);
      setStatus('Registration successful. Please login to continue.');
      setTimeout(() => navigate('/login'), 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to register.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page auth">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Register with invite code</h2>
          <p className="muted">Use the invite code sent after approval to activate your account.</p>
        </div>
        <form className="form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <Input label="Full name" name="name" value={form.name} onChange={handleChange} required />
            <Input label="Work email" name="email" type="email" value={form.email} onChange={handleChange} required />
            <Input label="Password" name="password" type="password" value={form.password} onChange={handleChange} required />
            <Select label="College" name="college_id" value={form.college_id} onChange={handleChange} required>
              <option value="">Select college</option>
              {colleges.map((college) => (
                <option key={college.college_id} value={college.college_id}>
                  {college.name_display}
                </option>
              ))}
            </Select>
            <Input label="City (optional)" name="city" value={form.city} onChange={handleChange} />
            <Input label="State (optional)" name="state" value={form.state} onChange={handleChange} />
            <Input label="Invite code" name="invite_code" value={form.invite_code} onChange={handleChange} required />
          </div>
          {alreadyAssigned ? (
            <div className="alert warning">
              This college already has an approved admin. Please contact the super admin.
            </div>
          ) : null}
          {status ? <div className="alert success">{status}</div> : null}
          {error ? <div className="alert error">{error}</div> : null}
          <button className="btn btn-primary" disabled={loading || alreadyAssigned} type="submit">
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        <p className="muted small">
          Have an account? <Link to="/login">Login</Link>.
        </p>
      </div>
    </div>
  );
}

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSession } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/auth/login', { ...form, role: 'college_admin' });
      await refreshSession();
      const redirectTo = location.state?.from || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/approval-required', { replace: true });
        return;
      }
      setError(err.response?.data?.error || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page auth">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Login to your dashboard</h2>
          <p className="muted">Access verified college insights and student readiness.</p>
        </div>
        <form className="form" onSubmit={handleSubmit}>
          <Input label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
          <Input label="Password" name="password" type="password" value={form.password} onChange={handleChange} required />
          {error ? <div className="alert error">{error}</div> : null}
          <button className="btn btn-primary" disabled={loading} type="submit">
            {loading ? 'Signing in...' : 'Login'}
          </button>
        </form>
        <div className="auth-footer">
          <Link to="/forgot-password">Forgot password?</Link>
          <span className="muted">No invite yet? <Link to="/access-request">Request access</Link>.</span>
        </div>
      </div>
    </div>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const response = await api.post('/auth/forgot-password', { email, role: 'college_admin' });
      setStatus(response.data?.message || 'If the email exists, a reset link has been sent.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page auth">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Reset your password</h2>
          <p className="muted">We will email you a secure reset link.</p>
        </div>
        <form className="form" onSubmit={handleSubmit}>
          <Input label="Email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          {status ? <div className="alert success">{status}</div> : null}
          {error ? <div className="alert error">{error}</div> : null}
          <button className="btn btn-primary" disabled={loading} type="submit">
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>
      </div>
    </div>
  );
}

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setStatus('');
    try {
      await api.post(`/auth/reset-password/${token}`, { password });
      setStatus('Password updated. Redirecting to login...');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page auth">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Create a new password</h2>
          <p className="muted">Use at least 6 characters.</p>
        </div>
        <form className="form" onSubmit={handleSubmit}>
          <Input label="New password" name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          {status ? <div className="alert success">{status}</div> : null}
          {error ? <div className="alert error">{error}</div> : null}
          <button className="btn btn-primary" disabled={loading} type="submit">
            {loading ? 'Updating...' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function ApprovalRequired() {
  return (
    <div className="page auth">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Approval required</h2>
          <p className="muted">
            Your college admin account is awaiting approval. Once approved, you will receive an invite code by email.
          </p>
        </div>
        <div className="cta-row">
          <Link className="btn btn-primary" to="/access-request">Request Access</Link>
          <Link className="btn btn-outline" to="/login">Back to Login</Link>
        </div>
      </div>
    </div>
  );
}

function DashboardLayout() {
  const { user, overview, setOverview, setUser, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!overview) {
        try {
          const response = await refreshSession();
          setOverview(response || null);
        } catch (error) {
          setUser(null);
          navigate('/login', { replace: true });
        }
      }
    };
    load();
  }, [overview, setOverview, setUser, navigate, refreshSession]);

  const handleLogout = async () => {
    await api.post('/auth/logout').catch(() => {});
    setUser(null);
    navigate('/login');
  };

  return (
    <div className="dashboard">
      <div className="mobile-topbar">
        <div className="brand">
          <div className="brand-mark">R</div>
          <div>
            <p className="brand-title">RankMySkills</p>
            <p className="brand-subtitle">College Admin</p>
          </div>
        </div>
        <button
          className="sidebar-toggle"
          onClick={() => setIsSidebarOpen((current) => !current)}
          type="button"
          aria-label="Toggle sidebar"
        >
          <i className={`bx ${isSidebarOpen ? 'bx-x' : 'bx-menu'} text-2xl`}></i>
        </button>
      </div>

      {isSidebarOpen ? (
        <button
          className="sidebar-backdrop"
          onClick={() => setIsSidebarOpen(false)}
          type="button"
          aria-label="Close sidebar"
        />
      ) : null}

      <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div>
          <div className="brand sidebar-brand">
            <div className="brand-mark">R</div>
            <div>
              <p className="brand-title">RankMySkills</p>
              <p className="brand-subtitle">College Admin</p>
            </div>
            <button
              className="sidebar-close"
              onClick={() => setIsSidebarOpen(false)}
              type="button"
              aria-label="Close sidebar"
            >
              <i className="bx bx-x text-2xl"></i>
            </button>
          </div>

          <nav className="sidebar-nav">
            <SidebarLink
              to="/dashboard"
              active={location.pathname === '/dashboard'}
              onClick={() => setIsSidebarOpen(false)}
            >
              Overview
            </SidebarLink>
            <SidebarLink
              to="/dashboard/global-leaderboard"
              active={location.pathname.includes('/global-leaderboard')}
              onClick={() => setIsSidebarOpen(false)}
            >
              Global Leaderboard
            </SidebarLink>
            <SidebarLink
              to="/dashboard/college-leaderboard"
              active={location.pathname.includes('/college-leaderboard')}
              onClick={() => setIsSidebarOpen(false)}
            >
              College Leaderboard
            </SidebarLink>
            <SidebarLink
              to="/dashboard/students"
              active={location.pathname.includes('/students')}
              onClick={() => setIsSidebarOpen(false)}
            >
              Students
            </SidebarLink>
            <SidebarLink
              to="/dashboard/assessments"
              active={location.pathname.includes('/assessments')}
              onClick={() => setIsSidebarOpen(false)}
            >
              Assessments
            </SidebarLink>
          </nav>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-stat">
            <p>Total students</p>
            <strong>{overview?.stats?.total_students ?? 0}</strong>
          </div>
          <button className="btn btn-outline btn-block" onClick={handleLogout} type="button">
            Logout
          </button>
        </div>
      </aside>

      <div className="dashboard-main">
        <div className="dashboard-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function Overview() {
  const { overview, refreshSession } = useAuth();
  const [filters, setFilters] = useState({ year: 'all', course: 'all' });
  const [filterOptions, setFilterOptions] = useState({ years: [], courses: [] });
  const [filteredOverview, setFilteredOverview] = useState(overview);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [yearsResponse, coursesResponse] = await Promise.all([
          api.get('/leaderboard/years'),
          api.get('/leaderboard/courses')
        ]);

        setFilterOptions({
          years: yearsResponse.data.years || [],
          courses: coursesResponse.data.courses || []
        });
      } catch (error) {
        console.error('Failed to fetch overview filters:', error);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    const loadOverview = async () => {
      setLoading(true);
      try {
        const response = await api.get('/college/overview', { params: filters });
        setFilteredOverview(response.data || null);
      } catch (error) {
        console.error('Failed to fetch college overview:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOverview();
  }, [filters, refreshSession]);

  const stats = filteredOverview?.stats || {};
  const college = filteredOverview?.college || overview?.college;
  const admin = filteredOverview?.admin || overview?.admin;
  const cards = [
    {
      key: 'students',
      label: 'Students',
      value: stats.total_students ?? 0,
      variant: 'count',
      icon: 'bx-group'
    },
    {
      key: 'engineer',
      label: 'Avg Engineering Score',
      value: stats.avg_engineer_score ?? 0,
      variant: 'engineer',
      icon: 'bx-line-chart'
    },
    {
      key: 'aptitude',
      label: 'Avg Aptitude',
      value: stats.avg_aptitude ?? 0,
      variant: 'skill',
      icon: 'bx-brain'
    },
    {
      key: 'verbal',
      label: 'Avg Verbal',
      value: stats.avg_verbal ?? 0,
      variant: 'skill',
      icon: 'bx-message-rounded-detail'
    },
    {
      key: 'problem_solving',
      label: 'Avg Problem Solving',
      value: stats.avg_problem_solving ?? 0,
      variant: 'skill',
      icon: 'bx-bulb'
    }
  ];

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-white/10 bg-gradient-to-r from-[#170c10] via-[#131317] to-[#101015] p-6 shadow-2xl">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-red-400">Overview</p>
            <h2 className="mt-2 text-3xl font-bold text-white">
              Hello, {admin?.name || 'College Admin'}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-300">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2">
                <i className="bx bx-buildings text-red-300"></i>
                {college?.name || 'Your College'}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-400/20">
                <i className="bx bx-badge-check"></i>
                Verified
              </span>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-400">
              Filter the entire overview by year and course to understand how each student segment is performing across engineering score and core skills.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <FilterSelect
              label="Year"
              value={filters.year}
              options={[{ value: 'all', label: 'All Years' }, ...filterOptions.years.map((year) => ({ value: String(year), label: String(year) }))]}
              onChange={(value) => setFilters((current) => ({ ...current, year: value }))}
            />
            <FilterSelect
              label="Course"
              value={filters.course}
              options={[{ value: 'all', label: 'All Courses' }, ...filterOptions.courses.map((course) => ({ value: course, label: course }))]}
              onChange={(value) => setFilters((current) => ({ ...current, course: value }))}
            />
          </div>
        </div>
      </section>

      {loading ? (
        <CenteredPanel text="Loading overview..." />
      ) : (
        <section className="grid gap-4 2xl:grid-cols-5 xl:grid-cols-3 md:grid-cols-2">
          {cards.map((card) => {
            const badge = card.variant === 'engineer'
              ? getEngineerBadgeMeta(card.value)
              : card.variant === 'skill'
                ? getSkillLevelMeta(card.value)
                : null;

            return (
              <article key={card.key} className="rounded-[24px] border border-white/10 bg-[#121217] p-5 shadow-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-900/30 text-red-300">
                    <i className={`bx ${card.icon} text-2xl`}></i>
                  </div>
                  {badge ? (
                    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${badge.classes}`}>
                      <i className={`bx ${badge.icon}`}></i>
                      {badge.label}
                    </span>
                  ) : null}
                </div>
                <p className="mt-4 text-sm font-medium text-slate-400">{card.label}</p>
                <p className="mt-2 text-3xl font-bold text-white">
                  {card.variant === 'count' ? card.value : `${card.value}${card.variant === 'engineer' ? ' / 1000' : ' / 100'}`}
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.24em] text-slate-500">
                  {card.variant === 'count' ? 'Filtered count' : 'Filtered average'}
                </p>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

function CollegeLeaderboard() {
  return (
    <LeaderboardPage
      type="college"
      title="College Leaderboard"
      subtitle="Exact college leaderboard experience adapted to the college dashboard theme."
      icon="bx-buildings"
      defaultSort="aptitude"
      sortOptions={[
        { value: 'aptitude', label: 'Aptitude Score' },
        { value: 'verbal', label: 'Verbal Score' },
        { value: 'problem_solving', label: 'Problem Solving Score' }
      ]}
    />
  );
}

function GlobalLeaderboard() {
  return (
    <LeaderboardPage
      type="global"
      title="Global Leaderboard"
      subtitle="Compare your college students with the broader RankMySkills ecosystem."
      icon="bx-world"
      includeCollegeFilter
      defaultSort="engineer_score"
      sortOptions={[
        { value: 'engineer_score', label: 'Global Engineer Score' },
        { value: 'cf', label: 'Codeforces Rating' },
        { value: 'lc', label: 'LeetCode Rating' },
        { value: 'cc', label: 'CodeChef Rating' }
      ]}
    />
  );
}

function LeaderboardPage({
  type,
  title,
  subtitle,
  icon,
  includeCollegeFilter = false,
  defaultSort,
  sortOptions
}) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [years, setYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [filters, setFilters] = useState({
    year: 'all',
    course: 'all',
    college: 'all',
    sort: defaultSort,
    page: 1
  });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const requests = [
          api.get('/leaderboard/years'),
          api.get('/leaderboard/courses')
        ];

        if (includeCollegeFilter) {
          requests.push(api.get('/auth/colleges'));
        }

        const responses = await Promise.all(requests);
        setYears(responses[0].data.years || []);
        setCourses(responses[1].data.courses || []);
        if (includeCollegeFilter) {
          setColleges(responses[2].data.colleges || []);
        }
      } catch (requestError) {
        console.error('Failed to fetch leaderboard filters:', requestError);
      }
    };

    loadFilters();
  }, [includeCollegeFilter]);

  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/leaderboard', {
          params: {
            type,
            year: filters.year,
            course: filters.course,
            college_id: includeCollegeFilter ? filters.college : undefined,
            sort: filters.sort,
            page: filters.page,
            limit: 50
          }
        });
        setLeaderboard(response.data.leaderboard || []);
        setMeta(response.data.meta || null);
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Failed to load leaderboard.');
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, [filters, includeCollegeFilter, type]);

  const scoreHeader = 'Global Score';

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-white/10 bg-[#121217] p-6 shadow-xl">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-red-400">{title}</p>
            <h2 className="mt-2 flex items-center gap-3 text-3xl font-bold text-white">
              <i className={`bx ${icon} text-red-300`}></i>
              {title}
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-400">{subtitle}</p>
          </div>
          <div className="grid gap-3 xl:grid-cols-4 md:grid-cols-2">
            <FilterSelect
              label="Year"
              value={filters.year}
              options={[{ value: 'all', label: 'All Years' }, ...years.map((year) => ({ value: String(year), label: String(year) }))]}
              onChange={(value) => setFilters((current) => ({ ...current, year: value, page: 1 }))}
            />
            <FilterSelect
              label="Course"
              value={filters.course}
              options={[{ value: 'all', label: 'All Courses' }, ...courses.map((course) => ({ value: course, label: course }))]}
              onChange={(value) => setFilters((current) => ({ ...current, course: value, page: 1 }))}
            />
            {includeCollegeFilter ? (
              <FilterSelect
                label="College"
                value={filters.college}
                options={[{ value: 'all', label: 'All Colleges' }, ...colleges.map((college) => ({ value: college.college_id, label: college.name_display }))]}
                onChange={(value) => setFilters((current) => ({ ...current, college: value, page: 1 }))}
              />
            ) : null}
            <FilterSelect
              label="Sort By"
              value={filters.sort}
              options={sortOptions}
              onChange={(value) => setFilters((current) => ({ ...current, sort: value, page: 1 }))}
            />
          </div>
        </div>
      </section>

      {loading ? <CenteredPanel text="Loading leaderboard..." /> : null}
      {!loading && error ? <CenteredPanel text={error} tone="error" /> : null}

      {!loading && !error ? (
        <>
          <div className="overflow-hidden rounded-[26px] border border-white/10 bg-[#121217] shadow-xl">
            <div className="overflow-x-auto">
              <table className={type === 'global' ? "w-full min-w-[700px] text-sm" : "w-full min-w-[900px]"}>
                <thead className="bg-white/[0.03]">
                  <tr>
                    <th>Rank</th>
                    <th>Student</th>
                    {type === 'global' ? <th>College</th> : null}
                    <th>Course</th>
                    <th>Year</th>
                    {type === 'global' ? (
                      <th>{scoreHeader}</th>
                    ) : null}
                    {type === 'global' ? (
                      <>
                        <th>Codeforces</th>
                        <th>LeetCode</th>
                        <th>CodeChef</th>
                      </>
                    ) : (
                      <>
                        <th>Aptitude</th>
                        <th>Verbal</th>
                        <th>Problem Solving</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.length === 0 ? (
                    <tr>
                      <td colSpan={type === 'global' ? 9 : 8} className="empty">No leaderboard data yet.</td>
                    </tr>
                  ) : (
                    leaderboard.map((student) => {
                      const engineerBadge = getEngineerBadgeMeta(student.global_engineer_score || 0);
                      return (
                        <tr key={student.id} className="hover:bg-white/[0.02]">
                          <td className="rank">#{student.rank}</td>
                          <td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-slate-300">
                                {student.profile_photo ? (
                                  <img
                                    src={student.profile_photo.startsWith('http') ? student.profile_photo : `${process.env.REACT_APP_IMG_URL || 'http://localhost:5000'}${student.profile_photo}`}
                                    alt={student.name}
                                    className="h-11 w-11 rounded-full object-cover"
                                  />
                                ) : (
                                  <i className="bx bx-user text-xl"></i>
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <strong className="text-white">{student.name}</strong>
                                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${engineerBadge.classes}`}>
                                    <i className={`bx ${engineerBadge.icon}`}></i>
                                    {engineerBadge.label}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-400">
                                  {type === 'college'
                                    ? `Average skill level ${getAverageSkillLevel(student.skill_levels)}`
                                    : 'Verified coding profile'}
                                </div>
                              </div>
                            </div>
                          </td>
                          {type === 'global' ? (
                            <td className="max-w-[180px] whitespace-normal break-words leading-tight">
                              {student.college}
                            </td>
                          ) : null}
                          <td>{student.course || 'NA'}</td>
                          <td>{student.graduation_year || 'NA'}</td>
                          {type === 'global' ? (
                            <td className="score">{student.global_engineer_score}</td>
                          ) : null}
                          {type === 'global' ? (
                            <>
                              <td>{student.ratings?.codeforces || '-'}</td>
                              <td>{student.ratings?.leetcode || '-'}</td>
                              <td>{student.ratings?.codechef || '-'}</td>
                            </>
                          ) : (
                            <>
                              <td>{student.skill_scores?.aptitude ?? 0}</td>
                              <td>{student.skill_scores?.verbal ?? 0}</td>
                              <td>{student.skill_scores?.problem_solving ?? 0}</td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination meta={meta} page={filters.page} onChange={(page) => setFilters((current) => ({ ...current, page }))} />
        </>
      ) : null}
    </div>
  );
}

function Students() {
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [years, setYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({
    year: 'all',
    course: 'all',
    engineer_band: 'all',
    aptitude_level: 'all',
    verbal_level: 'all',
    problem_solving_level: 'all',
    page: 1
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [yearsResponse, coursesResponse] = await Promise.all([
          api.get('/leaderboard/years'),
          api.get('/leaderboard/courses')
        ]);
        setYears(yearsResponse.data.years || []);
        setCourses(coursesResponse.data.courses || []);
      } catch (requestError) {
        console.error('Failed to load student filters:', requestError);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    const loadStudents = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/college/students', { params: { ...filters, limit: 50 } });
        setStudents(response.data.students || []);
        setMeta(response.data.meta || null);
      } catch (requestError) {
        setError(requestError.response?.data?.error || 'Failed to load students.');
      } finally {
        setLoading(false);
      }
    };

    loadStudents();
  }, [filters]);

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-white/10 bg-[#121217] p-6 shadow-xl">
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-red-400">Students</p>
            <h2 className="mt-2 flex items-center gap-3 text-3xl font-bold text-white">
              <i className="bx bx-group text-red-300"></i>
              Student Intelligence Table
            </h2>
            <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-400">
              Filter your college’s student base by year, course, engineer-score band, and skill levels to identify strong, average, and weak segments instantly.
            </p>
          </div>

          <div className="grid gap-3 2xl:grid-cols-6 xl:grid-cols-4 md:grid-cols-2">
            <FilterSelect
              label="Year"
              value={filters.year}
              options={[{ value: 'all', label: 'All Years' }, ...years.map((year) => ({ value: String(year), label: String(year) }))]}
              onChange={(value) => setFilters((current) => ({ ...current, year: value, page: 1 }))}
            />
            <FilterSelect
              label="Course"
              value={filters.course}
              options={[{ value: 'all', label: 'All Courses' }, ...courses.map((course) => ({ value: course, label: course }))]}
              onChange={(value) => setFilters((current) => ({ ...current, course: value, page: 1 }))}
            />
            <FilterSelect
              label="Engineer Score"
              value={filters.engineer_band}
              options={ENGINEER_FILTER_OPTIONS}
              onChange={(value) => setFilters((current) => ({ ...current, engineer_band: value, page: 1 }))}
            />
            <FilterSelect
              label="Aptitude"
              value={filters.aptitude_level}
              options={SKILL_FILTER_OPTIONS}
              onChange={(value) => setFilters((current) => ({ ...current, aptitude_level: value, page: 1 }))}
            />
            <FilterSelect
              label="Verbal"
              value={filters.verbal_level}
              options={SKILL_FILTER_OPTIONS}
              onChange={(value) => setFilters((current) => ({ ...current, verbal_level: value, page: 1 }))}
            />
            <FilterSelect
              label="Problem Solving"
              value={filters.problem_solving_level}
              options={SKILL_FILTER_OPTIONS}
              onChange={(value) => setFilters((current) => ({ ...current, problem_solving_level: value, page: 1 }))}
            />
          </div>
        </div>
      </section>

      {loading ? <CenteredPanel text="Loading students..." /> : null}
      {!loading && error ? <CenteredPanel text={error} tone="error" /> : null}

      {!loading && !error ? (
        <>
          <div className="overflow-hidden rounded-[26px] border border-white/10 bg-[#121217] shadow-xl">
            <div className="overflow-x-auto">
              <table className="min-w-[1100px]">
                <thead className="bg-white/[0.03]">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Course</th>
                    <th>Year</th>
                    <th>Engineer Score</th>
                    <th>Aptitude</th>
                    <th>Verbal</th>
                    <th>Problem Solving</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty">No students match the current filters.</td>
                    </tr>
                  ) : (
                    students.map((student) => {
                      const engineerBadge = getEngineerBadgeMeta(student.global_engineer_score || 0);
                      return (
                        <tr key={student._id} className="hover:bg-white/[0.02]">
                          <td>
                            <div className="flex flex-col gap-1">
                              <strong className="text-white">{student.name}</strong>
                              <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${engineerBadge.classes}`}>
                                <i className={`bx ${engineerBadge.icon}`}></i>
                                {engineerBadge.label}
                              </span>
                            </div>
                          </td>
                          <td>{student.email}</td>
                          <td>{student.course || 'NA'}</td>
                          <td>{student.graduation_year || 'NA'}</td>
                          <td className="score">{student.global_engineer_score ?? 0}</td>
                          <td>{renderSkillCell(student.skill_scores?.aptitude ?? 0)}</td>
                          <td>{renderSkillCell(student.skill_scores?.verbal ?? 0)}</td>
                          <td>{renderSkillCell(student.skill_scores?.problem_solving ?? 0)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination meta={meta} page={filters.page} onChange={(page) => setFilters((current) => ({ ...current, page }))} />
        </>
      ) : null}
    </div>
  );
}

function Assessments() {
  const [assessments, setAssessments] = useState([]);
  const [years, setYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({
    year: 'all',
    course: 'all',
    category: 'all'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    year: '',
    course: '',
    category: 'aptitude',
    date: '',
    max_marks: ''
  });
  const [csvRows, setCsvRows] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [unmatchedModal, setUnmatchedModal] = useState({ open: false, rows: [], title: '' });
  const [deleteState, setDeleteState] = useState({ loadingId: '', error: '' });
  const [expandedAssessmentId, setExpandedAssessmentId] = useState('');

  const loadFilters = useCallback(async () => {
    try {
      const [yearsResponse, coursesResponse] = await Promise.all([
        api.get('/leaderboard/years'),
        api.get('/leaderboard/courses')
      ]);
      setYears(yearsResponse.data.years || []);
      setCourses(coursesResponse.data.courses || []);
    } catch (requestError) {
      console.error('Failed to load assessment filters:', requestError);
    }
  }, []);

  const loadAssessments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/college/assessments', { params: filters });
      setAssessments(response.data.assessments || []);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to load assessments.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      const rows = parseCsvRows(text);
      setCsvRows(rows);
      setCsvFileName(file.name);
    } catch (parseError) {
      setError('Failed to parse CSV. Use only name, email, and marks columns.');
    }
  };

  const handleCreateAssessment = async (event) => {
    event.preventDefault();
    setSubmitLoading(true);
    setError('');
    setUploadSummary(null);

    try {
      const response = await api.post('/college/assessments', {
        name: createForm.name,
        year: createForm.year,
        course: createForm.course,
        category: createForm.category,
        assessment_date: createForm.date || undefined,
        max_marks: Number(createForm.max_marks),
        weightage: 100,
        scores: csvRows.map((row) => ({
          name: row.name,
          email: row.email,
          marks: row.marks
        }))
      });

      setUploadSummary(response.data.results || null);
      setShowCreateModal(false);
      setCreateForm({ name: '', year: '', course: '', category: 'aptitude', date: '', max_marks: '' });
      setCsvRows([]);
      setCsvFileName('');
      await loadAssessments();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to create assessment.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const openUnmatchedRows = async (assessment) => {
    try {
      const response = await api.get(`/college/assessments/${assessment._id}/unmatched`);
      setUnmatchedModal({
        open: true,
        rows: response.data.entries || [],
        title: assessment.name
      });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to load unmatched rows.');
    }
  };

  const handleDeleteAssessment = async (assessment) => {
    const confirmed = window.confirm('Do you want to delete this assessment? Changes are permanent.');
    if (!confirmed) {
      return;
    }

    setDeleteState({ loadingId: assessment._id, error: '' });
    try {
      await api.delete(`/college/assessments/${assessment._id}`);
      await loadAssessments();
    } catch (requestError) {
      setDeleteState({
        loadingId: '',
        error: requestError.response?.data?.error || 'Failed to delete assessment.'
      });
    } finally {
      setDeleteState((current) => ({ ...current, loadingId: '' }));
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-white/10 bg-[#121217] p-6 shadow-xl">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-red-400">Assessments</p>
            <h2 className="mt-2 text-3xl font-bold text-white">Upload assessment scores</h2>
            <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-400">
              Create an assessment, upload a simple CSV with name, email, and marks, and immediately push normalized scores into student dashboards.
            </p>
          </div>

          <div className="flex flex-col gap-3 xl:items-end">
            <div className="grid gap-3 xl:grid-cols-3 md:grid-cols-2">
              <FilterSelect
                label="Year"
                value={filters.year}
                options={[{ value: 'all', label: 'All Years' }, ...years.map((year) => ({ value: String(year), label: String(year) }))]}
                onChange={(value) => setFilters((current) => ({ ...current, year: value }))}
              />
              <FilterSelect
                label="Course"
                value={filters.course}
                options={[{ value: 'all', label: 'All Courses' }, ...courses.map((course) => ({ value: course, label: course }))]}
                onChange={(value) => setFilters((current) => ({ ...current, course: value }))}
              />
              <FilterSelect
                label="Type"
                value={filters.category}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'aptitude', label: 'Aptitude' },
                  { value: 'verbal', label: 'Verbal' },
                  { value: 'problem_solving', label: 'Problem Solving' }
                ]}
                onChange={(value) => setFilters((current) => ({ ...current, category: value }))}
              />
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <i className="bx bx-plus text-lg"></i>
              Add Assessment Score
            </button>
          </div>
        </div>
      </section>

      {uploadSummary ? (
        <section className="grid gap-4 md:grid-cols-2">
          <div className="rounded-3xl border border-emerald-400/20 bg-emerald-500/10 p-5 text-emerald-200">
            <p className="text-sm uppercase tracking-[0.2em]">Matched successfully</p>
            <p className="mt-2 text-3xl font-bold">{uploadSummary.matched_count} students marks updated</p>
          </div>
          <div className="rounded-3xl border border-rose-400/20 bg-rose-500/10 p-5 text-rose-200">
            <p className="text-sm uppercase tracking-[0.2em]">Not matched</p>
            <p className="mt-2 text-3xl font-bold">{uploadSummary.unmatched_count} student details not matched</p>
            {uploadSummary.unmatched_count > 0 ? (
              <button
                type="button"
                className="mt-4 rounded-full border border-rose-300/30 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
                onClick={() => setUnmatchedModal({
                  open: true,
                  rows: uploadSummary.unmatched_entries || [],
                  title: 'Latest upload'
                })}
              >
                See who they are
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {deleteState.error ? <CenteredPanel text={deleteState.error} tone="error" /> : null}
      {loading ? <CenteredPanel text="Loading assessments..." /> : null}
      {!loading && error ? <CenteredPanel text={error} tone="error" /> : null}

      {!loading && !error ? (
        <section className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {assessments.length === 0 ? (
            <div className="panel center lg:col-span-2 2xl:col-span-3">
              <p>No assessments found for the current filters.</p>
            </div>
          ) : (
            assessments.map((assessment) => {
              const categoryBadge = getAssessmentCategoryMeta(assessment.category);
              const isExpanded = expandedAssessmentId === assessment._id;
              return (
                <article key={assessment._id} className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-lg">
                  <div className="flex items-start justify-between gap-4">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${categoryBadge.badge}`}>
                      <i className={`bx ${categoryBadge.icon} text-2xl`}></i>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteAssessment(assessment)}
                      className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-rose-300 hover:text-rose-500"
                    >
                      <i className={`bx ${deleteState.loadingId === assessment._id ? 'bx-loader-alt animate-spin' : 'bx-trash'} text-lg`}></i>
                    </button>
                  </div>

                  <div className="mt-4">
                    <h3 className="text-xl font-bold text-slate-900">{assessment.name}</h3>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                      <span className={`rounded-full px-3 py-1 ${categoryBadge.badge}`}>{categoryBadge.label}</span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">{assessment.target_graduation_year || 'NA'}</span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">{assessment.target_course || 'All Courses'}</span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">
                        {assessment.assessment_date ? new Date(assessment.assessment_date).toLocaleDateString() : 'NA'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setExpandedAssessmentId((current) => current === assessment._id ? '' : assessment._id)}
                      className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                    >
                      {isExpanded ? 'Hide details' : 'Show details'}
                    </button>
                    {assessment.unmatched_count > 0 ? (
                      <button
                        type="button"
                        onClick={() => openUnmatchedRows(assessment)}
                        className="rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-100"
                      >
                        View unmatched entries
                      </button>
                    ) : null}
                  </div>

                  {isExpanded ? (
                    <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                      <AssessmentStatCard label="Matched" value={assessment.matched_count || 0} tone="success" />
                      <AssessmentStatCard label="Unmatched" value={assessment.unmatched_count || 0} tone="danger" />
                      <AssessmentStatCard label="Rows Uploaded" value={assessment.uploaded_rows || 0} tone="neutral" />
                    </div>
                  ) : null}
                </article>
              );
            })
          )}
        </section>
      ) : null}

      {showCreateModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-3xl rounded-[28px] border border-white/10 bg-[#121217] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-red-400">New assessment</p>
                <h3 className="mt-2 text-2xl font-bold text-white">Create and upload marks</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-full bg-white/5 p-2 text-slate-300 transition hover:bg-white/10"
              >
                <i className="bx bx-x text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleCreateAssessment} className="mt-6 space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <Input label="Assessment name" name="name" value={createForm.name} onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))} required />
                <Select label="Year" name="year" value={createForm.year} onChange={(event) => setCreateForm((current) => ({ ...current, year: event.target.value }))} required>
                  <option value="">Select year</option>
                  {years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Select>
                <Select label="Course" name="course" value={createForm.course} onChange={(event) => setCreateForm((current) => ({ ...current, course: event.target.value }))} required>
                  <option value="">Select course</option>
                  {courses.map((course) => (
                    <option key={course} value={course}>{course}</option>
                  ))}
                </Select>
                <Select label="Category" name="category" value={createForm.category} onChange={(event) => setCreateForm((current) => ({ ...current, category: event.target.value }))} required>
                  <option value="aptitude">Aptitude</option>
                  <option value="verbal">Verbal</option>
                  <option value="problem_solving">Problem Solving</option>
                </Select>
                <Input label="Assessment date" name="date" type="date" value={createForm.date} onChange={(event) => setCreateForm((current) => ({ ...current, date: event.target.value }))} />
                <Input label="Max marks" name="max_marks" type="number" min="1" value={createForm.max_marks} onChange={(event) => setCreateForm((current) => ({ ...current, max_marks: event.target.value }))} required />
                <label className="field">
                  <span>CSV file</span>
                  <input type="file" accept=".csv" className="input" onChange={handleCsvUpload} required />
                  <small className="muted">Only `name,email,marks` columns are needed.</small>
                </label>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/15 p-4 text-sm text-slate-300">
                <p className="font-semibold text-white">Upload preview</p>
                <p className="mt-2">{csvFileName ? `${csvFileName} loaded with ${csvRows.length} rows` : 'No CSV selected yet.'}</p>
              </div>

              <div className="flex justify-end gap-3">
                <button type="button" className="btn btn-outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitLoading || !csvRows.length}>
                  {submitLoading ? 'Uploading...' : 'Post Assessment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {unmatchedModal.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl rounded-[28px] border border-white/10 bg-[#121217] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-rose-400">Unmatched entries</p>
                <h3 className="mt-2 text-2xl font-bold text-white">{unmatchedModal.title}</h3>
              </div>
              <button
                type="button"
                onClick={() => setUnmatchedModal({ open: false, rows: [], title: '' })}
                className="rounded-full bg-white/5 p-2 text-slate-300 transition hover:bg-white/10"
              >
                <i className="bx bx-x text-xl"></i>
              </button>
            </div>

            <div className="mt-6 overflow-hidden rounded-3xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-[0.2em] text-slate-400">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatchedModal.rows.map((row, index) => (
                    <tr key={`${row.email}-${index}`} className="border-t border-white/10 text-slate-200">
                      <td className="px-4 py-3">{row.name}</td>
                      <td className="px-4 py-3">{row.email}</td>
                      <td className="px-4 py-3">{row.marks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AssessmentStatCard({ label, value, tone }) {
  const toneClasses = tone === 'danger'
    ? 'border-rose-200 bg-rose-50 text-rose-600'
    : tone === 'neutral'
      ? 'border-slate-200 bg-slate-50 text-slate-700'
      : 'border-emerald-200 bg-emerald-50 text-emerald-600';

  return (
    <div className={`rounded-2xl border p-3 ${toneClasses}`}>
      <p className="text-xs uppercase tracking-[0.18em]">{label}</p>
      <p className="mt-2 text-2xl font-bold">{value}</p>
    </div>
  );
}

function getAssessmentCategoryMeta(category) {
  switch (category) {
    case 'aptitude':
      return {
        label: 'Aptitude',
        icon: 'bx-brain',
        badge: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      };
    case 'verbal':
      return {
        label: 'Verbal',
        icon: 'bx-message-rounded-detail',
        badge: 'bg-rose-100 text-rose-700 border border-rose-200',
      };
    default:
      return {
        label: 'Problem Solving',
        icon: 'bx-bulb',
        badge: 'bg-amber-100 text-amber-700 border border-amber-200',
      };
  }
}

function parseCsvRows(text) {
  const [headerLine, ...lines] = text.split(/\r?\n/).filter(Boolean);
  if (!headerLine) {
    return [];
  }

  const headers = headerLine.split(',').map((cell) => cell.trim().toLowerCase());
  const nameIndex = headers.findIndex((header) => header === 'name');
  const emailIndex = headers.findIndex((header) => header === 'email');
  const marksIndex = headers.findIndex((header) => header === 'marks');

  if (nameIndex === -1 || emailIndex === -1 || marksIndex === -1) {
    throw new Error('Invalid CSV format');
  }

  return lines.map((line) => {
    const cells = line.split(',').map((cell) => cell.trim());
    return {
      name: cells[nameIndex] || '',
      email: cells[emailIndex] || '',
      marks: Number(cells[marksIndex] || 0)
    };
  }).filter((row) => row.name || row.email || row.marks);
}


function FilterSelect({ label, value, options, onChange }) {
  return (
    <label className="flex min-w-[180px] flex-col gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-2xl border border-white/10 bg-[#0f0f13] px-4 py-3 text-sm font-medium normal-case tracking-normal text-white outline-none transition focus:border-red-500/60"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pagination({ meta, page, onChange }) {
  if (!meta || meta.total_pages <= 1) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-3">
      <button
        className="btn btn-outline"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        type="button"
      >
        Previous
      </button>
      <span className="text-sm text-slate-400">
        Page {meta.page} of {meta.total_pages}
      </span>
      <button
        className="btn btn-outline"
        disabled={page >= meta.total_pages}
        onClick={() => onChange(page + 1)}
        type="button"
      >
        Next
      </button>
    </div>
  );
}

function getSortColumnLabel(sort) {
  switch (sort) {
    case 'aptitude':
      return 'Aptitude';
    case 'verbal':
      return 'Verbal';
    case 'problem_solving':
      return 'Problem Solving';
    default:
      return 'Global Score';
  }
}

function getEngineerBadgeMeta(score) {
  if (score >= 900) return { label: 'Elite', icon: 'bx-crown', classes: 'bg-yellow-500/15 text-yellow-300 border border-yellow-400/20' };
  if (score >= 800) return { label: 'Expert', icon: 'bx-medal', classes: 'bg-purple-500/15 text-purple-300 border border-purple-400/20' };
  if (score >= 700) return { label: 'Advanced', icon: 'bx-trophy', classes: 'bg-indigo-500/15 text-indigo-300 border border-indigo-400/20' };
  if (score >= 550) return { label: 'Strong', icon: 'bx-star', classes: 'bg-sky-500/15 text-sky-300 border border-sky-400/20' };
  if (score >= 400) return { label: 'Intermediate', icon: 'bx-trending-up', classes: 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/20' };
  if (score >= 200) return { label: 'Learner', icon: 'bx-book-open', classes: 'bg-orange-500/15 text-orange-300 border border-orange-400/20' };
  return { label: 'Beginner', icon: 'bx-rocket', classes: 'bg-slate-500/15 text-slate-300 border border-slate-400/20' };
}

function getSkillLevelMeta(score) {
  if (score > 80) return { label: 'Level 5', icon: 'bx-crown', classes: 'bg-yellow-500/15 text-yellow-300 border border-yellow-400/20' };
  if (score > 60) return { label: 'Level 4', icon: 'bx-diamond', classes: 'bg-cyan-500/15 text-cyan-300 border border-cyan-400/20' };
  if (score > 40) return { label: 'Level 3', icon: 'bx-badge-check', classes: 'bg-rose-500/15 text-rose-300 border border-rose-400/20' };
  if (score > 20) return { label: 'Level 2', icon: 'bx-medal', classes: 'bg-amber-500/15 text-amber-300 border border-amber-400/20' };
  return { label: 'Level 1', icon: 'bx-shield-quarter', classes: 'bg-slate-500/15 text-slate-300 border border-slate-400/20' };
}

function getAverageSkillLevel(levels = {}) {
  const values = [levels.aptitude, levels.verbal, levels.problem_solving].filter(Boolean);
  if (!values.length) {
    return 'Level 1';
  }
  return `Level ${Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)}`;
}

function renderSkillCell(score) {
  const badge = getSkillLevelMeta(score);
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className="font-semibold text-white">{score}</span>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold whitespace-nowrap ${badge.classes}`}>
        <i className={`bx ${badge.icon}`}></i>
        {badge.label}
      </span>
    </div>
  );
}

const ENGINEER_FILTER_OPTIONS = [
  { value: 'all', label: 'All Bands' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'learner', label: 'Learner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'strong', label: 'Strong' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' },
  { value: 'elite', label: 'Elite' }
];

const SKILL_FILTER_OPTIONS = [
  { value: 'all', label: 'All Levels' },
  { value: '1', label: 'Level 1' },
  { value: '2', label: 'Level 2' },
  { value: '3', label: 'Level 3' },
  { value: '4', label: 'Level 4' },
  { value: '5', label: 'Level 5' }
];

function Input({ label, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input {...props} className="input" />
    </label>
  );
}

function Select({ label, children, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select {...props} className="input">
        {children}
      </select>
    </label>
  );
}

function TextArea({ label, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea {...props} className="input textarea" rows="4" />
    </label>
  );
}

function CenteredPanel({ text, tone }) {
  return (
    <div className={`panel center ${tone === 'error' ? 'panel-error' : ''}`}>
      <p>{text}</p>
    </div>
  );
}

function SidebarLink({ to, active, children, onClick }) {
  return (
    <Link className={`sidebar-link ${active ? 'active' : ''}`} to={to} onClick={onClick}>
      {children}
    </Link>
  );
}

export default App;
